import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search, Lock, Crown } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CompanionCard from "@/components/CompanionCard";
import { Reveal } from "@/components/Reveal";
import SEO from "@/components/SEO";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SORT_OPTIONS = [
  { value: "featured", label: "Featured" },
  { value: "rating", label: "Highest Rated" },
];

const flatTrigger =
  "rounded-none border-white/15 bg-transparent text-white focus:ring-velvet-gold data-[placeholder]:text-white/40";
const flatContent = "rounded-none border-white/15 bg-velvet-coal text-white";

export default function Directory() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [companions, setCompanions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [vibes, setVibes] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cats, setCats] = useState([]);
  const [races, setRaces] = useState([]);
  const [genders, setGenders] = useState([]);

  const isPremium = !!user && (user.role === "admin" || user.membership === "premium" || user.account_type === "advertiser");
  const canFilter = isPremium; // free members = name-search only

  useEffect(() => {
    if (user === false) navigate("/login");
  }, [user, navigate]);

  const province = params.get("province") || "all";
  const city = params.get("city") || "all";
  const town = params.get("town") || "all";
  const sort = params.get("sort") || "featured";
  const verifiedOnly = params.get("verified") === "true";
  const availableNow = params.get("available_now") === "true";
  const vibe = params.get("vibe") || "";
  const category = params.get("category") || "";
  const race = params.get("race") || "all";
  const gender = params.get("gender") || "all";

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (!value || value === "all" || value === false) next.delete(key);
    else next.set(key, value);
    // reset dependents
    if (key === "province") { next.delete("city"); next.delete("town"); }
    if (key === "city") next.delete("town");
    setParams(next);
  };

  useEffect(() => {
    api.get("/vibes").then((r) => setVibes(r.data)).catch(() => {});
    api.get("/locations").then((r) => setProvinces(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data.map((c) => c.name))).catch(() => {});
    api.get("/races").then((r) => setRaces(r.data)).catch(() => {});
    api.get("/genders").then((r) => setGenders(r.data)).catch(() => {});
  }, []);

  // derived options
  const allCities = provinces.flatMap((p) =>
    p.cities.map((c) => ({ ...c, province: p.name }))
  );
  const cityOptions =
    province === "all"
      ? allCities
      : allCities.filter((c) => c.province === province);
  const townOptions =
    city === "all" ? [] : (allCities.find((c) => c.slug === city)?.towns || []);

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const q = new URLSearchParams();
    if (canFilter) {
      if (province !== "all") q.set("province", province);
      if (city !== "all") q.set("city", city);
      if (town !== "all") q.set("town", town);
      if (category) q.set("category", category);
      if (race !== "all") q.set("race", race);
      if (gender !== "all") q.set("gender", gender);
      if (availableNow) q.set("available_now", "true");
      if (verifiedOnly) q.set("verified", "true");
      if (vibe) q.set("vibe", vibe);
    }
    if (sort) q.set("sort", sort);
    if (search) q.set("search", search);
    try {
      const { data } = await api.get(`/companions?${q.toString()}`);
      setCompanions(data);
    } catch (_err) {
      // Auth or network hiccup — leave the previous list visible, no toast.
      setCompanions([]);
    } finally {
      setLoading(false);
    }
  }, [user, canFilter, province, city, town, category, race, gender, availableNow, sort, verifiedOnly, vibe, search]);

  useEffect(() => {
    const t = setTimeout(fetchData, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [fetchData, search]);

  return (
    <div className="pt-28 md:pt-36" data-testid="directory-page">
      <SEO
        title="Companion Directory"
        description="Explore Vixera's curated directory of verified companions across South Africa. Filter by city, category, rate and availability. Discreet on-platform messaging."
      />
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <Reveal>
          <p className="mb-4 text-xs uppercase tracking-luxe text-velvet-gold">The Directory</p>
          <h1 className="font-heading text-5xl font-semibold leading-none tracking-tight text-white md:text-7xl">
            Discover your <span className="italic text-velvet-rose">match</span>
          </h1>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-12 lg:grid-cols-12">
          {/* Filters */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-28 space-y-8">
              <div>
                <label className="mb-3 block text-xs uppercase tracking-luxe text-white/40">Search by name</label>
                <div className="flex items-center gap-3 border-b border-white/15 pb-2">
                  <Search size={16} className="text-white/40" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={canFilter ? "Name, tagline or interest" : "Type a companion's name"}
                    data-testid="directory-search-input"
                    className="w-full bg-transparent text-sm text-white placeholder:text-white/30 focus:outline-none"
                  />
                </div>
              </div>

              {!canFilter && (
                <div className="border border-velvet-gold/40 bg-velvet-gold/5 p-5" data-testid="filter-upgrade-card">
                  <div className="flex items-start gap-3">
                    <Lock className="mt-1 text-velvet-gold" size={16} />
                    <div>
                      <p className="text-[10px] uppercase tracking-luxe text-velvet-gold">Premium filters locked</p>
                      <p className="mt-2 text-xs font-light leading-relaxed text-white/60">
                        Free members can search by name only. Upgrade to Premium to filter by city, gender, race, category, vibes, verified, available now and more.
                      </p>
                      <Link
                        to="/membership"
                        data-testid="filter-upgrade-btn"
                        className="mt-4 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[10px] uppercase tracking-luxe text-black hover:bg-velvet-gold hover:text-white"
                      >
                        <Crown size={12} /> Upgrade to premium
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {canFilter && <>
              <div>
                <label className="mb-3 block text-xs uppercase tracking-luxe text-white/40">Gender</label>
                <Select value={gender} onValueChange={(v) => setParam("gender", v)}>
                  <SelectTrigger className={flatTrigger} data-testid="directory-gender-select">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent className={flatContent}>
                    <SelectItem value="all">Any gender</SelectItem>
                    {genders.map((g) => (<SelectItem key={g} value={g}>{g}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="mb-3 block text-xs uppercase tracking-luxe text-white/40">Province</label>
                <Select value={province} onValueChange={(v) => setParam("province", v)}>
                  <SelectTrigger className={flatTrigger} data-testid="directory-province-select">
                    <SelectValue placeholder="All Provinces" />
                  </SelectTrigger>
                  <SelectContent className={flatContent}>
                    <SelectItem value="all">All Provinces</SelectItem>
                    {provinces.map((p) => (
                      <SelectItem key={p.slug} value={p.name}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="mb-3 block text-xs uppercase tracking-luxe text-white/40">City</label>
                <Select value={city} onValueChange={(v) => setParam("city", v)}>
                  <SelectTrigger className={flatTrigger} data-testid="directory-city-select">
                    <SelectValue placeholder="All Cities" />
                  </SelectTrigger>
                  <SelectContent className={`${flatContent} max-h-72`}>
                    <SelectItem value="all">All Cities</SelectItem>
                    {cityOptions.map((c) => (
                      <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {townOptions.length > 0 && (
                <div>
                  <label className="mb-3 block text-xs uppercase tracking-luxe text-white/40">Town / Area</label>
                  <Select value={town} onValueChange={(v) => setParam("town", v)}>
                    <SelectTrigger className={flatTrigger} data-testid="directory-town-select">
                      <SelectValue placeholder="All Areas" />
                    </SelectTrigger>
                    <SelectContent className={`${flatContent} max-h-72`}>
                      <SelectItem value="all">All Areas</SelectItem>
                      {townOptions.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <label className="mb-3 block text-xs uppercase tracking-luxe text-white/40">Sort by</label>
                <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
                  <SelectTrigger className={flatTrigger} data-testid="directory-sort-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={flatContent}>
                    {SORT_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="mb-3 block text-xs uppercase tracking-luxe text-white/40">Category</label>
                <div className="flex flex-wrap gap-2" data-testid="directory-category-filters">
                  {cats.map((c) => {
                    const activeCat = category === c;
                    return (
                      <button key={c} onClick={() => setParam("category", activeCat ? "" : c)}
                        data-testid={`category-chip-${c.toLowerCase()}`}
                        className={`rounded-full border px-4 py-2 text-[11px] uppercase tracking-luxe transition-colors duration-300 ${
                          activeCat ? "border-velvet-gold bg-velvet-gold text-black" : "border-white/15 text-white/60 hover:border-velvet-gold hover:text-velvet-gold"
                        }`}>{c}</button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="mb-3 block text-xs uppercase tracking-luxe text-white/40">Ethnicity</label>
                <Select value={race} onValueChange={(v) => setParam("race", v)}>
                  <SelectTrigger className={flatTrigger} data-testid="directory-race-select">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent className={`${flatContent} max-h-72`}>
                    <SelectItem value="all">Any ethnicity</SelectItem>
                    {races.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>

              <button
                onClick={() => setParam("available_now", !availableNow)}
                data-testid="directory-available-toggle"
                className={`w-full rounded-none border px-5 py-3 text-xs uppercase tracking-luxe transition-colors duration-300 ${
                  availableNow ? "border-velvet-rose bg-velvet-rose text-black" : "border-white/15 text-white/70 hover:border-white/40"
                }`}
              >
                Available now
              </button>

              <button
                onClick={() => setParam("verified", !verifiedOnly)}
                data-testid="directory-verified-toggle"
                className={`w-full rounded-none border px-5 py-3 text-xs uppercase tracking-luxe transition-colors duration-300 ${
                  verifiedOnly ? "border-velvet-gold bg-velvet-gold text-black" : "border-white/15 text-white/70 hover:border-white/40"
                }`}
              >
                Verified only
              </button>

              <div>
                <label className="mb-3 block text-xs uppercase tracking-luxe text-white/40">Vibe</label>
                <div className="flex flex-wrap gap-2" data-testid="directory-vibe-filters">
                  {vibes.map((v) => {
                    const active = vibe === v;
                    return (
                      <button
                        key={v}
                        onClick={() => setParam("vibe", active ? "" : v)}
                        data-testid={`vibe-chip-${v.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`rounded-full border px-4 py-2 text-[11px] uppercase tracking-luxe transition-colors duration-300 ${
                          active
                            ? "border-velvet-rose bg-velvet-rose text-black"
                            : "border-white/15 text-white/60 hover:border-velvet-rose hover:text-velvet-rose"
                        }`}
                      >
                        {v}
                      </button>
                    );
                  })}
                </div>
              </div>
              </>}
            </div>
          </aside>

          {/* Grid */}
          <div className="lg:col-span-9">
            <p className="mb-8 text-xs uppercase tracking-luxe text-white/40" data-testid="directory-count">
              {loading ? "Loading…" : `${companions.length} companions`}
            </p>
            {!loading && companions.length === 0 ? (
              <p className="py-24 text-center font-heading text-3xl font-light text-white/40">
                No companions match your search.
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                {companions.map((c, i) => (
                  <CompanionCard key={c.id} companion={c} index={i} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
